"use client"

import type React from "react"
import { CheckCircle2, Package, Star } from "lucide-react"
import { ComplaintFilePreview } from "../complaint/ComplaintFilePreview.tsx"
import type { MaterialUsed } from "../inventory/materialUsageUtils.ts"
import { isStoredUploadPath, materialDisplayLabel, materialOutcomeLabel } from "../inventory/materialUsageUtils.ts"

type ComplaintResolutionSummaryProps = {
  complaintId?: string
  status?: string
  resolvedAt?: string | null
  remarks?: string | null
  feedbackComments?: string | null
  resolutionProof?: string | null
  materials?: MaterialUsed[] | null
  resolutionAttempts?: number | null
  satisfactionRating?: number | null
  collectedAmount?: number | null
  collectedAt?: string | null
  billingInvoiceNumber?: string | null
  /** When false, only parts/billing rows (for view modal where remarks/proof already show). */
  includeContextSections?: boolean
}

const RESOLVED_STATUSES = new Set(["resolved", "completed", "closed"])

export function ComplaintResolutionSummary({
  complaintId,
  status,
  resolvedAt,
  remarks,
  feedbackComments,
  resolutionProof,
  materials,
  resolutionAttempts,
  satisfactionRating,
  collectedAmount,
  collectedAt,
  billingInvoiceNumber,
  includeContextSections = true,
}: ComplaintResolutionSummaryProps) {
  const rows = materials ?? []
  const isResolved = RESOLVED_STATUSES.has(String(status || ""))
  const hasProof = isStoredUploadPath(resolutionProof)
  const hasBilling =
    rows.length > 0 ||
    (collectedAmount != null && collectedAmount > 0) ||
    Boolean(billingInvoiceNumber)
  const show =
    hasBilling ||
    (includeContextSections &&
      (isResolved ||
        Boolean(remarks?.trim()) ||
        Boolean(feedbackComments?.trim()) ||
        hasProof ||
        satisfactionRating != null))

  if (!show) return null

  return (
    <div className="space-y-3 border-t border-slate-200/80 pt-4">
      {includeContextSections ? (
        <>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-green" aria-hidden />
            <h3 className="text-sm font-semibold text-deep-ocean">Resolution record</h3>
          </div>

          {resolvedAt ? (
            <p className="text-xs text-slate-gray">Resolved {new Date(resolvedAt).toLocaleString()}</p>
          ) : null}

          {resolutionAttempts != null && resolutionAttempts > 0 ? (
            <p className="text-xs text-slate-gray">Resolution attempts: {resolutionAttempts}</p>
          ) : null}

          {remarks?.trim() ? (
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-gray">
                Resolution remarks
              </p>
              <p className="rounded-lg border border-slate-200/80 bg-slate-50 px-3 py-2 text-sm text-slate-gray">
                {remarks}
              </p>
            </div>
          ) : null}

          {feedbackComments?.trim() ? (
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-gray">
                Customer feedback
              </p>
              <p className="rounded-lg border border-slate-200/80 bg-slate-50 px-3 py-2 text-sm text-slate-gray">
                {feedbackComments}
              </p>
            </div>
          ) : null}

          {satisfactionRating != null ? (
            <p className="flex items-center gap-1.5 text-sm text-slate-gray">
              <Star className="h-3.5 w-3.5 text-golden-amber" aria-hidden />
              Satisfaction: {satisfactionRating}/5
            </p>
          ) : null}
        </>
      ) : (
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-electric-blue" aria-hidden />
          <h3 className="text-sm font-semibold text-deep-ocean">Parts & billing</h3>
        </div>
      )}

      {billingInvoiceNumber ? (
        <p className="text-sm text-slate-gray">
          Linked invoice: <span className="font-medium text-deep-ocean">{billingInvoiceNumber}</span>
        </p>
      ) : null}

      {collectedAmount != null && collectedAmount > 0 ? (
        <p className="text-sm text-slate-gray">
          Cash collected: Rs {Number(collectedAmount).toLocaleString()}
          {collectedAt ? ` · ${new Date(collectedAt).toLocaleString()}` : ""}
        </p>
      ) : includeContextSections && isResolved ? (
        <p className="text-sm text-slate-gray">Cash collected: None</p>
      ) : null}

      <div>
        <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-gray">
          <Package className="h-3.5 w-3.5" aria-hidden />
          Parts used
        </p>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-gray">{isResolved ? "None recorded" : "—"}</p>
        ) : (
          <ul className="space-y-1.5">
            {rows.map((row, index) => {
              const outcome = materialOutcomeLabel(row.usage_outcome)
              return (
                <li
                  key={`${row.item_type}-${index}`}
                  className="flex items-center justify-between gap-2 rounded-lg border border-slate-200/80 bg-white px-3 py-2"
                >
                  <span className="min-w-0 text-sm text-deep-ocean">{materialDisplayLabel(row)}</span>
                  {outcome ? (
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
                        row.usage_outcome === "installed"
                          ? "bg-emerald-green/10 text-emerald-green"
                          : "bg-slate-gray/10 text-slate-gray"
                      }`}
                    >
                      {outcome}
                    </span>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
        <p className="mt-1.5 text-[11px] text-slate-gray/80">
          Recorded when the complaint was resolved in the employee portal or billing flow.
        </p>
      </div>

      {hasProof && complaintId && includeContextSections ? (
        <ComplaintFilePreview
          label="Resolution proof"
          filePath={resolutionProof!}
          fetchUrl={`/complaints/resolution-proof/${complaintId}`}
        />
      ) : null}
    </div>
  )
}
